import { PaginatedResponse } from './Deploy';

export type ActivityExportRequestType = {
  request: {
    email: string;
    format_id: number;
    public_key: string;
    start_of_period: string;
    tax_period_id: number;
  };
  signature: string;
};

export type ActivityExportRequestDataResult = {
  account_public_key: string;
  created_at: string;
  email: string;
  error_message: string;
  format_id: number;
  id: number;
  name: string;
  start_of_period: string;
  status_id: number;
  tax_period_id: number;
  updated_at: string;
};

export type ActivityExportRequestError = {
  code: string;
  description: string;
  message: string;
};

export interface ActivityExportRequestResult {
  data: ActivityExportRequestDataResult;
  error: ActivityExportRequestError;
}

export type ActivityExportDeployStatusesResult = { id: number; name: string }[];

export interface ActivityExportEntitlementNFTMetadataEntry {
  key: string;
  value: string;
}

export interface ActivityExportEntitlementNFTMetadata {
  description: string;
  image_url: string;
  name: string;
  token_id: string;
  year: number;
}

export type ActivityExportEntitlementNFTEntryType = {
  block_height: number;
  metadata: ActivityExportEntitlementNFTMetadataEntry[];
  contract_package_hash: string;
  is_burned: boolean;
  offchain_metadata_status: number;
  offchain_metadata: Record<string, any>;
  onchain_metadata: Record<string, any>;
  owner_hash: string;
  owner_public_key: string;
  owner_type: number;
  timestamp: string;
  token_id: string;
  token_standard_id: number;
  tracking_id: string;
};

export type ActivityExportEntitlementNFTType = {
  block_height: number;
  contract_package_hash: string;
  is_burned: boolean;
  metadata: ActivityExportEntitlementNFTMetadata;
  offchain_metadata_status: number;
  offchain_metadata: Record<string, any>;
  onchain_metadata: Record<string, any>;
  owner_hash: string;
  owner_public_key: string;
  owner_type: number;
  timestamp: string;
  token_id: string;
  token_standard_id: number;
  tracking_id: string;
};

export type ActivityExportRecentRequest = {
  account_public_key: string;
  created_at: string;
  format_id: number;
  id: number;
  start_of_period: string;
  status_id: number;
  tax_period_id: number;
  updated_at: string;
};

export const MapMetadata = ({
  data,
  ...rest
}: PaginatedResponse<ActivityExportEntitlementNFTEntryType>): PaginatedResponse<ActivityExportEntitlementNFTType> => ({
  ...rest,
  data: data
    ? data.map((item) => ({
        ...item,
        metadata: item.metadata
          ? (
              item.metadata as ActivityExportEntitlementNFTMetadataEntry[]
            ).reduce(
              (prev, curr) => ({
                ...prev,
                [curr.key.trim()]: curr.value,
              }),
              {} as ActivityExportEntitlementNFTMetadata
            )
          : (item.metadata as ActivityExportEntitlementNFTMetadata),
      }))
    : [],
});

export enum TaxPeriodType {
  Year = 'Year',
  Quarter = 'Quarter',
  Month = 'Month',
  Week = 'Week',
  Day = 'Day',
}

export const taxPeriodIdMap = {
  [TaxPeriodType.Year]: 5,
  [TaxPeriodType.Quarter]: 4,
  [TaxPeriodType.Month]: 3,
  [TaxPeriodType.Week]: 2,
  [TaxPeriodType.Day]: 1,
};

export const taxPeriodMapIdToName = {
  5: TaxPeriodType.Year,
  4: TaxPeriodType.Quarter,
  3: TaxPeriodType.Month,
  2: TaxPeriodType.Week,
  1: TaxPeriodType.Day,
};

export const activityTypeList = [
  TaxPeriodType.Year,
  TaxPeriodType.Quarter,
  TaxPeriodType.Month,
];

export const activityPeriodList = {
  [TaxPeriodType.Quarter]: ['Q4', 'Q3', 'Q2', 'Q1'],
  [TaxPeriodType.Month]: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

export const allAvailableYears = [2024, 2023, 2022, 2021];

export const DEFAULT_YEAR = 2024;

export const quarterStartDate = {
  Q1: 0,
  Q2: 3,
  Q3: 6,
  Q4: 9,
};

export const quarterMapStartDateToName = {
  0: 'Q1',
  3: 'Q2',
  6: 'Q3',
  9: 'Q4',
};

export enum FormatType {
  CoinLedger = 'CoinLedger',
  Koinly = 'Koinly',
  CoinTracker = 'CoinTracker',
  CSPRLive = 'CSPR.live',
}

export const formatIdMap = {
  [FormatType.CoinLedger]: 1,
  [FormatType.Koinly]: 2,
  [FormatType.CoinTracker]: 3,
  [FormatType.CSPRLive]: 4,
};

export const formatMapIdToName = {
  1: FormatType.CoinLedger,
  2: FormatType.Koinly,
  3: FormatType.CoinTracker,
  4: FormatType.CSPRLive,
};

export const activityFormatList = [
  FormatType.Koinly,
  FormatType.CoinLedger, // disabled, until reports will be fixed
  FormatType.CoinTracker,
  FormatType.CSPRLive,
];
